import { Auth, Hub } from "aws-amplify";
import { message } from "antd";
import { useEffect, useState, useCallback, useRef } from "react";
import { useDispatch } from "react-redux";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { getAccountDetails, getAccountByEmail } from "Sdk/Actions";
import { login } from "Store/reducers/Auth/AuthSlice";
import { LoginForm } from "@/Components/login-from";
import { Loader } from "@/Components/Loader";
import Logo from "@/Assets/Logo/logo.png";
import AuthImage from "@/Assets/auth-image.png";
import {
  clearGoogleAccountLinkRetryAttempt,
  clearGoogleOAuthPending,
  getCombinedOAuthErrorStringFromWindow,
  getFederatedSignInFailureUserMessage,
  hasGoogleAccountLinkRetryAttempted,
  isAccountLinkedOAuthError,
  shouldShowAccountLinkedUiToast,
  isGoogleAccountLinkFirstPassFailure,
  isGoogleOAuthPending,
  isOAuthInvalidGrantError,
  markGoogleAccountLinkRetryAttempted,
  markOAuthInvalidGrantRejected,
  consumeOAuthInvalidGrantRejected,
  clearOAuthInvalidGrantSessionFlag,
  setGoogleOAuthPending,
  stripOAuthCallbackParamsFromUrl,
  getLoginErrorMessage,
  getGooglePostSignInSetupErrorMessage,
} from "Utility/authErrors";

export default function Login() {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const [isSubmitLoading, setIsSubmitLoading] = useState(false);
  const [isGoogleLoading, setIsGoogleLoading] = useState(false);
  const [loginError, setLoginError] = useState(false);
  const stateLoginAttempted = useRef(false);
  const isGoogleFlowRef = useRef(false);
  const googleLinkAutoRetryRef = useRef(false);
  const googleOauthInvalidGrantRef = useRef(false);
  const search = location.search || "";
  const hash = window.location.hash || "";
  const isGoogleAuthCallback =
    search.includes("code=") ||
    search.includes("error=") ||
    hash.includes("id_token") ||
    hash.includes("access_token") ||
    hash.includes("error=");


  const handleSubmit = useCallback(
    async (data) => {
      setIsSubmitLoading(true);
      setLoginError(false);
      isGoogleFlowRef.current = false;
      clearGoogleOAuthPending();
      try {
        const res = await Auth.signIn(data?.email, data?.password);
        const username = res?.attributes?.preferred_username || "";

        if (!username) {
          navigate("/onboarding", { replace: true });
          return;
        }

        const account = await getAccountDetails(username);
        if (!account) {
          navigate("/onboarding", { replace: true });
          return;
        }

        dispatch(
          login({
            token: res?.signInUserSession?.accessToken?.jwtToken,
          }),
        );

        navigate("/dashboard");
      } catch (err) {
        setLoginError(true);
        if (err?.message === "User is not confirmed.") {
          message.info("Seems like your account is not verified yet.");
          navigate("/confirm-signup", { state: { email: data?.email } });
        } else {
          const email = String(data?.email || "").trim();
          let accountByEmail = null;
          if (email) {
            try {
              accountByEmail = await getAccountByEmail(email);
            } catch (_) {
              /* ignore */
            }
          }

          const isUserNotFound =
            err?.code === "UserNotFoundException" ||
            String(err?.message || "").toLowerCase().includes("user does not exist");

          const isNotAuthorized = err?.code === "NotAuthorizedException";

          if (accountByEmail?.username) {
            if (isUserNotFound) {
              message.error(
                "This email is registered with Google sign-in (or a Google-linked account). Please use “Sign in with Google” below, not email and password.",
              );
              return;
            }
            if (isNotAuthorized) {
              message.error(
                "Incorrect password. If this email only uses Google sign-in, use “Sign in with Google” below instead.",
              );
              return;
            }
          }

          message.error(getLoginErrorMessage(err));
        }
      } finally {
        setIsSubmitLoading(false);
      }
    },
    [dispatch, navigate],
  );

  const handleGoogleLogin = useCallback(async () => {
    googleOauthInvalidGrantRef.current = false;
    clearOAuthInvalidGrantSessionFlag();
    clearGoogleAccountLinkRetryAttempt();
    stripOAuthCallbackParamsFromUrl();
    setGoogleOAuthPending();
    googleLinkAutoRetryRef.current = false;
    isGoogleFlowRef.current = true;
    setIsGoogleLoading(true);
    try {
      await Auth.federatedSignIn({ provider: "Google" });
    } catch (err) {
      setIsGoogleLoading(false);
      clearGoogleOAuthPending();
      console.error("Google sign-in failed:", err);
      if (isOAuthInvalidGrantError(err)) {
        googleOauthInvalidGrantRef.current = true;
        markOAuthInvalidGrantRejected();
      }
      message.error(getFederatedSignInFailureUserMessage(err));
    }
  }, []);

  const maybeRetryGoogleAfterAccountLink = useCallback(async (hubPayload) => {
    if (
      !isGoogleAccountLinkFirstPassFailure(hubPayload) &&
      !isGoogleAccountLinkFirstPassFailure(hubPayload?.data)
    ) {
      return false;
    }
    if (hasGoogleAccountLinkRetryAttempted() || googleLinkAutoRetryRef.current) {
      return false;
    }
    googleLinkAutoRetryRef.current = true;
    markGoogleAccountLinkRetryAttempted();
    isGoogleFlowRef.current = true;
    setIsGoogleLoading(true);
    try {
      stripOAuthCallbackParamsFromUrl();
      await Auth.federatedSignIn({ provider: "Google" });
      return true;
    } catch (err) {
      console.error("Google link retry sign-in failed:", err);
      if (isOAuthInvalidGrantError(err)) {
        googleOauthInvalidGrantRef.current = true;
        markOAuthInvalidGrantRejected();
      }
      message.error(getFederatedSignInFailureUserMessage(err));
      setIsGoogleLoading(false);
      isGoogleFlowRef.current = false;
      clearGoogleOAuthPending();
      return true;
    }
  }, []);

  const finishGoogleLogin = useCallback(async () => {
    const urlErrEarly = getCombinedOAuthErrorStringFromWindow();
    if (googleOauthInvalidGrantRef.current || isOAuthInvalidGrantError(urlErrEarly)) {
      googleOauthInvalidGrantRef.current = false;
      const e = new Error("invalid_grant");
      e.code = "invalid_grant";
      throw e;
    }

    let authUser;
    try {
      authUser = await Auth.currentAuthenticatedUser({ bypassCache: true });
    } catch (_) {
      authUser = await Auth.currentAuthenticatedUser();
    }

    const attrs = authUser?.attributes || {};
    const tokenPayload =
      authUser?.signInUserSession?.idToken?.payload ||
      authUser?.signInUserSession?.accessToken?.payload ||
      {};
    const email = String(attrs.email || tokenPayload.email || "").trim();

    let profileUsername = String(
      attrs.preferred_username || tokenPayload.preferred_username || "",
    ).trim();

    const poolUsername = String(
      tokenPayload["cognito:username"] || authUser?.username || "",
    ).trim();
    if (
      !profileUsername &&
      poolUsername &&
      !/^google_/i.test(poolUsername) &&
      !/^facebook_/i.test(poolUsername)
    ) {
      profileUsername = poolUsername;
    }

    if (!profileUsername && email) {
      const row = await getAccountByEmail(email);
      if (row?.username) {
        profileUsername = String(row.username).trim();
      }
    }

    if (!profileUsername) {
      const wl = typeof window !== "undefined" ? window.location : { search: "", hash: "" };
      const ws = wl.search || "";
      const wh = wl.hash || "";
      const fromOauthCallback =
        ws.includes("code=") ||
        ws.includes("error=") ||
        wh.includes("id_token") ||
        wh.includes("access_token") ||
        wh.includes("error=");

      if (fromOauthCallback) {
        await new Promise((r) => setTimeout(r, 150));
      }

      const urlErr = getCombinedOAuthErrorStringFromWindow();
      if (
        consumeOAuthInvalidGrantRejected() ||
        googleOauthInvalidGrantRef.current ||
        isOAuthInvalidGrantError(urlErr)
      ) {
        googleOauthInvalidGrantRef.current = false;
        message.error(getFederatedSignInFailureUserMessage({ message: "invalid_grant" }));
        navigate({ pathname: "/login" }, { replace: true });
        return;
      }

      navigate("/onboarding", { replace: true });
      return;
    }

    let account = await getAccountDetails(profileUsername);
    if (!account && email) {
      const row = await getAccountByEmail(email);
      if (row?.username && row.username !== profileUsername) {
        account = await getAccountDetails(row.username);
      }
    }
    if (!account) {
      navigate("/onboarding", { replace: true });
      return;
    }

    dispatch(
      login({
        token: authUser?.signInUserSession?.accessToken?.jwtToken,
      }),
    );

    navigate("/dashboard", { replace: true });
  }, [dispatch, navigate]);

  const stateEmail = location?.state?.email;
  const statePassword = location?.state?.password;
  const stateMessage = location?.state?.message;

  useEffect(() => {
    const msg = stateMessage || sessionStorage.getItem("loginMessage");
    if (msg) {
      message.info(msg);
      sessionStorage.removeItem("loginMessage");
    }
  }, [stateMessage]);

  useEffect(() => {
    const unsubscribe = Hub.listen("auth", async ({ payload }) => {
      switch (payload.event) {
        case "signIn":
        case "cognitoHostedUI":
          try {
            clearGoogleAccountLinkRetryAttempt();
            clearGoogleOAuthPending();
            googleLinkAutoRetryRef.current = false;
            setIsGoogleLoading(true);
            await finishGoogleLogin();
          } catch (err) {
            console.error("Error handling signed-in user:", err);
            clearGoogleOAuthPending();
            if (isOAuthInvalidGrantError(err)) {
              googleOauthInvalidGrantRef.current = true;
              message.error(getFederatedSignInFailureUserMessage(err));
              navigate({ pathname: "/login" }, { replace: true });
            } else {
              message.error(getGooglePostSignInSetupErrorMessage(err));
            }
          } finally {
            setIsGoogleLoading(false);
            isGoogleFlowRef.current = false;
          }
          break;
        case "signIn_failure":
        case "cognitoHostedUI_failure":
          if (!isGoogleFlowRef.current && !isGoogleAuthCallback && !isGoogleOAuthPending()) {
            break;
          }
          console.error("Google sign-in failed:", payload.data);
          {
            const failureBlob = payload?.data || payload;
            if (isOAuthInvalidGrantError(failureBlob)) {
              googleOauthInvalidGrantRef.current = true;
              markOAuthInvalidGrantRejected();
            }
            if (
              isAccountLinkedOAuthError(failureBlob) &&
              !hasGoogleAccountLinkRetryAttempted() &&
              shouldShowAccountLinkedUiToast()
            ) {
              message.info(getFederatedSignInFailureUserMessage(failureBlob), 8);
            }
            const retried = await maybeRetryGoogleAfterAccountLink(payload);
            if (retried) {
              break;
            }
          }
          message.error(getFederatedSignInFailureUserMessage(payload?.data || payload));
          setIsGoogleLoading(false);
          isGoogleFlowRef.current = false;
          clearGoogleOAuthPending();
          break;
        default:
          break;
      }
    });

    return () => unsubscribe();
  }, [
    finishGoogleLogin,
    isGoogleAuthCallback,
    maybeRetryGoogleAfterAccountLink,
    navigate,
  ]);

  useEffect(() => {
    if (!isGoogleAuthCallback) return;

    setIsGoogleLoading(true);

    let isCancelled = false;

    const urlGrantErr = getCombinedOAuthErrorStringFromWindow();
    if (isOAuthInvalidGrantError(urlGrantErr)) {
      googleOauthInvalidGrantRef.current = true;
      markOAuthInvalidGrantRejected();
      message.error(getFederatedSignInFailureUserMessage(urlGrantErr));
      setIsGoogleLoading(false);
      clearGoogleOAuthPending();
      clearGoogleAccountLinkRetryAttempt();
      navigate({ pathname: "/login" }, { replace: true });
      return;
    }

    const decodedErr = getCombinedOAuthErrorStringFromWindow();
    if (
      decodedErr &&
      isGoogleAccountLinkFirstPassFailure(decodedErr) &&
      !hasGoogleAccountLinkRetryAttempted() &&
      !googleLinkAutoRetryRef.current
    ) {
      googleLinkAutoRetryRef.current = true;
      markGoogleAccountLinkRetryAttempted();
      isGoogleFlowRef.current = true;
      if (isAccountLinkedOAuthError(decodedErr) && shouldShowAccountLinkedUiToast()) {
        message.info(getFederatedSignInFailureUserMessage(decodedErr), 8);
      }
      void (async () => {
        try {
          stripOAuthCallbackParamsFromUrl();
          await Auth.federatedSignIn({ provider: "Google" });
        } catch (err) {
          if (isCancelled) return;
          console.error("Google link retry (from URL error) failed:", err);
          if (isOAuthInvalidGrantError(err)) {
            googleOauthInvalidGrantRef.current = true;
            markOAuthInvalidGrantRejected();
          }
          message.error(getFederatedSignInFailureUserMessage(err));
          setIsGoogleLoading(false);
          isGoogleFlowRef.current = false;
          clearGoogleOAuthPending();
        }
      })();
      return () => {
        isCancelled = true;
      };
    }

    const resolveGoogleLogin = async (retries = 8) => {
      try {
        await finishGoogleLogin();
      } catch (err) {
        if (isCancelled) return;

        if (retries > 0) {
          window.setTimeout(() => {
            resolveGoogleLogin(retries - 1);
          }, 800);
          return;
        }

        console.error("Google callback handling failed:", err);
        if (isOAuthInvalidGrantError(err)) {
          googleOauthInvalidGrantRef.current = true;
          markOAuthInvalidGrantRejected();
          message.error(getFederatedSignInFailureUserMessage(err));
        } else {
          message.error(getGooglePostSignInSetupErrorMessage(err));
        }
        setIsGoogleLoading(false);
        clearGoogleOAuthPending();
        navigate({ pathname: "/login" }, { replace: true });
      }
    };

    resolveGoogleLogin();

    return () => {
      isCancelled = true;
    };
  }, [finishGoogleLogin, isGoogleAuthCallback, navigate, location.search, location.hash]);

  useEffect(() => {
    if (stateLoginAttempted.current) return;
    if (stateEmail && statePassword) {
      stateLoginAttempted.current = true;
      handleSubmit({ email: stateEmail, password: statePassword });
    }
  }, [handleSubmit, stateEmail, statePassword]);

  if ((location?.state?.directLogin && !loginError) || isGoogleLoading) {
    return (
      <div className="flex min-h-svh flex-col items-center justify-center gap-2 p-6">
        <Loader />
        <p className="text-muted-foreground text-center text-sm">
          {isGoogleLoading
            ? hasGoogleAccountLinkRetryAttempted()
              ? "Connecting your Google account to your existing profile…"
              : "Hold tight while we verify your Google sign-in and redirect you."
            : "Hold tight while we set up your account."}
        </p>
      </div>
    );
  }


  return (
    <div className="grid min-h-svh lg:grid-cols-2">
      <div className="flex flex-col gap-4 p-6 md:p-10">
        <div className="flex justify-center gap-2 md:justify-start">
          <Link
            to="/"
            className="flex items-center gap-2 font-semibold"
            aria-label="Inprofile home"
          >
            <img src={Logo} alt="Inprofile" className="h-8 w-8" />
            Inprofile
          </Link>
        </div>
        <div className="flex flex-1 items-center justify-center">
          <div className="w-full max-w-xs">
            <LoginForm
              onSubmit={handleSubmit}
              onGoogleLogin={handleGoogleLogin}
              isSubmitLoading={isSubmitLoading}
              isGoogleLoading={isGoogleLoading}
              initialEmail={stateEmail}
              initialPassword={statePassword}
            />
          </div>
        </div>
      </div>
      <div className="bg-[#f2f5f8] relative hidden lg:block">
        <img
          src={AuthImage}
          alt=""
          className="absolute inset-0 h-full w-full object-contain dark:brightness-[0.2] dark:grayscale"
        />
      </div>
    </div>
  );
}
